<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, sameAs } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'
import FileUpload from '../../Shared/FileUpload.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// validation rules
const rules = {
  umsokn: {
    hasDoctorsNote: { required },
    hasDoctorsNoteDate: {
      required: requiredIf(() => umsokn.value?.hasDoctorsNote === false)
    },
    fileDisclamer: { sameAs: sameAs(true) }
  }
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})

</script>

<template>
  <div v-if="umsokn">
    <h2 class="form__header">{{ t('ororku_umsokn_step10_header') }}</h2>

    <!-- Doctor's note radio -->
    <v-radio-group v-model="umsokn.hasDoctorsNote" :readonly="props.disable" inline hide-details>
      <v-radio :label="t('ororku_umsokn_step10_FileDoctor')" :value="true" />
      <v-radio :label="t('ororku_umsokn_step10_NoFileDoctor')" :value="false" />
    </v-radio-group>
    <div v-if="v$.umsokn.hasDoctorsNote.$error">
      <div class="error__text">{{ t('ororku_umsokn_step10_hasNoteErrorText') }}</div>
    </div>

    <!-- File upload if has note -->
    <div v-if="umsokn.hasDoctorsNote === true" class="mt-4">
      <FileUpload
        :umsoknType="3"
        :umsoknId="umsokn.id"
        :filespace="0"
        :disable="props.disable"
      />
    </div>

    <!-- Date if no doctor's note -->
    <div v-if="umsokn.hasDoctorsNote === false" class="mt-4">
      <DatePicker
        v-model="umsokn.hasDoctorsNoteDate"
        :disabled="props.disable"
        :label="t('ororku_umsokn_step10_hasNoteDate')"
        :error="v$.umsokn.hasDoctorsNoteDate.$error"
        :max="null"
        :error-messages="v$.umsokn.hasDoctorsNoteDate.$error ? [t('ororku_umsokn_step10_hasNoteDate_date')] : []"
      />
    </div>

    <!-- Other files -->
    <div class="mt-6">
      <label>{{ t('ororku_umsokn_step10_FileOther') }}</label>
      <FileUpload
        :umsoknType="3"
        :umsoknId="umsokn.id"
        :filespace="2"
        :disable="props.disable"
      />
    </div>

    <!-- Disclaimer -->
    <v-row class="mt-6">
      <v-col>
        <v-checkbox
          v-model="umsokn.fileDisclamer"
          :readonly="props.disable"
          :label="t('ororku_umsokn_step11_file_disclaimer')"
          :error="v$.umsokn.fileDisclamer.$error"
          :error-messages="v$.umsokn.fileDisclamer.$error ? [t('ororku_umsokn_step11_file_disclaimer_error')] : []"
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.mt-4 {
  margin-top: 1rem;
}
.mt-6 {
  margin-top: 1.5rem;
}
</style>
